import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock } from 'lucide-react';
import CTA from '../../components/CTA';

const LisburnHypnotherapy: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Hypnotherapy Lisburn | Clinic at The Arzen Collective</title>
        <meta
          name="description"
          content="Solution-focused hypnotherapy in Lisburn at The Arzen Collective, Antrim Street. Anxiety, stress, weight management, chronic pain. CNHC registered. Early evening appointments available."
        />
        <meta name="keywords" content="hypnotherapy Lisburn, hypnotherapist Lisburn, anxiety treatment Lisburn, quit smoking Lisburn, weight loss Lisburn, stress management Lisburn, Arzen Collective Lisburn" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/lisburn-hypnotherapy/" />
        <meta property="og:title" content="Hypnotherapy Lisburn | Clinic at The Arzen Collective" />
        <meta property="og:description" content="Solution-focused hypnotherapy in Lisburn at The Arzen Collective on Antrim Street. Anxiety, stress, weight management, chronic pain." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Lisburn",
            "description": "CNHC registered solution-focused hypnotherapy in Lisburn, based at The Arzen Collective on Antrim Street. Specializing in anxiety, stress management, weight management, quit smoking, and chronic pain.",
            "url": "https://www.sharonclare-hypnotherapy.com/lisburn-hypnotherapy/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "areaServed": {
              "@type": "City",
              "name": "Lisburn",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "The Arzen Collective",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "The Arzen Collective, 13B Antrim Street",
                "addressLocality": "Lisburn",
                "addressRegion": "County Antrim",
                "postalCode": "BT28",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ],
            "medicalSpecialty": "Clinical Hypnotherapy",
            "serviceType": [
              "Anxiety Treatment",
              "Stress Management",
              "Weight Management",
              "Smoking Cessation",
              "Chronic Pain Management",
              "Medical Procedure Anxiety"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Hypnotherapy in Lisburn
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Now at The Arzen Collective, Antrim Street
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block text-left">
              <div className="flex items-start space-x-4 mb-3">
                <MapPin className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">The Arzen Collective</span>
                  <br />
                  13B Antrim Street, Lisburn, BT28
                </p>
              </div>
              <div className="flex items-start space-x-4">
                <Clock className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <p className="text-lg text-gray-700">
                  Appointment times vary — early evening appointments available
                </p>
              </div>
              <p className="text-gray-600 mt-3">Online sessions also available</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Hypnotherapy for Lisburn
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Sharon Clare now sees clients in Lisburn at{' '}
                <a
                  href="https://arzencollective.co.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-600 hover:text-accent-700 underline"
                >
                  The Arzen Collective
                </a>{' '}
                on Antrim Street — so specialist hypnotherapy is available locally, without the drive
                into Belfast.
              </p>
              <p>
                The Arzen Collective is built around the nervous system, bringing together
                practitioners who work in complementary ways. That means your care can be joined up:
                a pathway built with more than one practitioner, rather than a single appointment in
                isolation. For anyone whose stress, anxiety or pain shows up in the body as much as
                the mind, that combined approach can make a real difference.
              </p>
              <p>
                As Northern Ireland's fifth-largest city and a major commuter hub for Belfast, Lisburn faces unique challenges. The pressures of daily commuting, work-related stress, maintaining work-life balance, and the fast pace of modern life take their toll on mental and physical health. Many Lisburn residents juggle demanding careers, family responsibilities, and financial pressures, leaving little time for self-care until stress, anxiety, or lifestyle conditions become unmanageable.
              </p>
              <p>
                Solution-focused hypnotherapy offers an evidence-based approach to managing these challenges. Whether you're dealing with commuter stress and anxiety, struggling with weight management, trying to quit smoking, managing chronic pain, or facing work-related burnout, hypnotherapy can help you regain control and make lasting positive changes.
              </p>
              <p>
                Sessions take place at The Arzen Collective on Antrim Street, in the centre of
                Lisburn. Appointment times vary from week to week and early evening appointments are
                available, which suits clients who can&rsquo;t get away during the working day.
                Online sessions are also available if that works better for you, and there is a
                second clinic at{' '}
                <a href="/belfast-hypnotherapy" className="text-accent-600 hover:text-accent-700 underline">
                  Belfast Wellness Clinic on the Ormeau Road
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon for Lisburn */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Choose Sharon Clare Hypnotherapy for Lisburn?
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  CNHC Registered Professional
                </h3>
                <p className="text-gray-700">
                  As a CNHC registered clinical hypnotherapist with nursing background, I provide evidence-based treatment that meets the highest professional standards. Many Lisburn professionals appreciate knowing they're working with a qualified practitioner who combines clinical expertise with a science-backed approach to therapy. You can trust that your treatment is safe, ethical, and effective.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  A Clinic in Lisburn Itself
                </h3>
                <p className="text-gray-700">
                  Sessions take place at The Arzen Collective on Antrim Street, in the centre of Lisburn — no drive into Belfast and no city centre routes to navigate. For clients who would rather come to the Ormeau Road, the Belfast clinic remains available, and online sessions are an option either way.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Appointments That Fit Around Work
                </h3>
                <p className="text-gray-700">
                  Appointment times vary from week to week, and early evening appointments are available — useful if you can&rsquo;t get away during the working day. Whether you prefer a morning session, something over lunch, or an early evening slot, get in touch and we can usually find a time that works around your schedule.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Understanding Commuter Stress
                </h3>
                <p className="text-gray-700">
                  Commuter stress is real - the daily journey to Belfast, traffic pressures, work demands, and trying to maintain work-life balance create chronic stress for many Lisburn residents. I understand these pressures and how they manifest as anxiety, sleep problems, irritability, and physical symptoms. Solution-focused hypnotherapy addresses both the stress itself and your ability to cope with daily pressures more effectively.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Specialist Medical Anxiety Support
                </h3>
                <p className="text-gray-700">
                  Our Belfast clinic is ideal for Lisburn residents who need support with medical anxiety, radiotherapy stress, or pre-surgery nerves. Drawing on extensive volunteer experience with Belfast City Hospital's Radiotherapy Department, I understand the challenges patients face and provide specialist support to help you feel calm and in control throughout your treatment.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Evidence-Based Results-Focused Approach
                </h3>
                <p className="text-gray-700">
                  Many Lisburn professionals prefer the evidence-based, solution-focused approach that hypnotherapy offers. Rather than lengthy analysis of problems, we concentrate on practical solutions, measurable change, and building the mental resilience you need to handle life's challenges. The neuroscience-backed techniques appeal to logical, results-oriented clients who want effective treatment that respects their time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services for Lisburn Clients */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Hypnotherapy Services for Lisburn Clients
            </h2>
            <div className="space-y-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                  Anxiety and Stress Management for Professionals
                </h3>
                <p className="text-gray-700 mb-4">
                  Commuter stress and work-related anxiety are among the most common issues affecting Lisburn residents. The daily commute to Belfast, workplace pressures, tight deadlines, demanding managers, job insecurity, and the constant juggling of work and family responsibilities create chronic stress that affects your sleep, health, relationships, and overall quality of life. Many professionals push through until they experience panic attacks, burnout, or physical symptoms that force them to seek help.
                </p>
                <p className="text-gray-700 mb-4">
                  Solution-focused hypnotherapy for anxiety works by addressing your brain's stress response at a neurological level. Rather than just teaching you to "cope better" with impossible demands, we work on genuinely reducing your anxiety levels, improving your sleep quality, building resilience to stress, and helping you develop a healthier relationship with work pressures. The evidence-based approach combines CBT principles with hypnotherapy for powerful, lasting results.
                </p>
                <p className="text-gray-700">
                  For Lisburn's working population, the results are often transformative. Clients report sleeping better, feeling calmer during their commute, handling workplace stress more effectively, experiencing fewer panic symptoms, and regaining a sense of control over their lives. Most notice significant improvements within 4-6 sessions, with many achieving results faster than traditional talking therapies. The convenient Belfast location means you can easily fit sessions around your work schedule.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                  Weight Management and Emotional Eating
                </h3>
                <p className="text-gray-700 mb-4">
                  Weight management is a common concern for Lisburn's suburban population, where busy lifestyles, commuter stress, and limited time for exercise create perfect conditions for weight gain. If you've tried every diet, lost weight only to regain it, struggle with emotional eating during stressful times, or feel trapped in unhealthy eating patterns, you're not alone. The problem isn't willpower - it's about addressing the psychological and emotional drivers behind your eating behaviors.
                </p>
                <p className="text-gray-700 mb-4">
                  Hypnotherapy for weight management works by changing your mindset and relationship with food at a subconscious level. We focus on understanding why you eat when you're not hungry, breaking the connection between emotions and eating, building genuine motivation for healthier choices, and creating sustainable behavior change that doesn't feel like deprivation. Unlike diets that focus on restriction, hypnotherapy helps you want to make healthier choices naturally.
                </p>
                <p className="text-gray-700">
                  Many Lisburn clients find this approach particularly effective because it fits into their busy professional lives without requiring complex meal plans or time-consuming food preparation. By addressing stress, emotional eating triggers, and unhealthy habits simultaneously, you create lasting change that survives the pressures of commuter life and work stress. Clients typically work through a 6-8 session programme, with many losing 1-2 stone and maintaining their weight loss long-term.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                  Quit Smoking and Vaping Programme
                </h3>
                <p className="text-gray-700 mb-4">
                  Smoking and vaping remain common habits among professionals despite widespread knowledge of health risks. The stress of work and commuting, habitual breaks with colleagues, and the psychological association between smoking and stress relief make quitting feel impossibly difficult. Many Lisburn professionals have tried to quit multiple times, often succeeding for weeks or months before returning to smoking during stressful periods.
                </p>
                <p className="text-gray-700 mb-4">
                  Hypnotherapy for smoking cessation is highly effective because it addresses the psychological addiction rather than just the physical nicotine dependence. The physical withdrawal from nicotine lasts only 3-5 days - it's the mental habit, the emotional associations, and the belief that you "need" cigarettes that keeps you smoking. By changing these subconscious patterns, breaking the stress-smoking connection, and building your identity as a confident non-smoker, success rates are significantly higher than willpower alone.
                </p>
                <p className="text-gray-700">
                  For Lisburn professionals, the financial benefits are substantial. A 20-a-day smoker saves thousands of pounds a year by quitting - money that could go toward family holidays, home improvements, or savings. Combined with improved health, better fitness, no more guilt or shame, and the pride of breaking free from addiction, quitting smoking is truly life-changing. Most clients work through a 3-4 session programme, with success rates of 60-80% for motivated clients who genuinely want to quit.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                  Chronic Pain Management
                </h3>
                <p className="text-gray-700 mb-4">
                  Chronic pain conditions such as back pain from long commutes, fibromyalgia, arthritis, migraines, and nerve pain are surprisingly common among Lisburn's working population. The stress of commuting exacerbates pain, poor posture during long drives contributes to back problems, and the constant pressure of work life increases muscle tension and pain sensitivity. Living with persistent pain affects every aspect of life - work performance, family relationships, sleep quality, and mental health.
                </p>
                <p className="text-gray-700 mb-4">
                  Neuroscience research shows that pain is processed in the brain, and when we're stressed or anxious, our brain amplifies pain signals. Solution-focused hypnotherapy for pain management works by calming your nervous system, reducing stress and anxiety that intensify pain, teaching your brain healthier pain processing patterns, and helping you develop better coping strategies. While hypnotherapy doesn't "cure" chronic conditions, many clients experience significant reductions in pain levels and improved quality of life.
                </p>
                <p className="text-gray-700">
                  The professional clinical environment at our Belfast clinic, combined with proximity to Belfast City Hospital for those under medical care, makes hypnotherapy an accessible complementary treatment for Lisburn residents managing chronic pain. Many clients find that addressing the stress component of their pain while learning relaxation and pain management techniques provides relief that medication alone couldn't achieve. A typical pain management programme involves 6-10 sessions, with most clients noticing improvements within the first month.
                </p>
              </div>
            </div>

            <div className="text-center mt-12">
              <a href="/services" className="btn-primary text-lg px-8 py-4 inline-flex items-center">
                View All Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* The Lisburn Clinic */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              The Lisburn Clinic
            </h2>
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white rounded-2xl p-8">
                <div className="flex items-start space-x-4 mb-4">
                  <MapPin className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-lg">Where to find us</h3>
                    <p className="text-gray-700 mb-2">
                      <span className="font-semibold">The Arzen Collective</span>
                    </p>
                    <p className="text-gray-700 mb-2">13B Antrim Street</p>
                    <p className="text-gray-700 mb-2">Lisburn, BT28</p>
                    <a
                      href="https://arzencollective.co.uk"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent-600 hover:text-accent-700 font-medium"
                    >
                      Visit The Arzen Collective →
                    </a>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-4 mt-4">
                  <p className="text-gray-700 text-sm">
                    Antrim Street sits in the centre of Lisburn, so sessions are easy to fit around
                    work, school runs or a trip into town.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8">
                <div className="flex items-start space-x-4 mb-4">
                  <Clock className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-lg">Appointments</h3>
                    <p className="text-gray-700 mb-2">
                      Appointment times vary from week to week, so the best way to arrange a session
                      is to get in touch directly.
                    </p>
                    <p className="text-gray-700 mb-2">
                      <span className="font-semibold">Early evening appointments available.</span>
                    </p>
                    <p className="text-gray-700">Online sessions also available.</p>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-4 mt-4">
                  <p className="text-gray-700 text-sm">
                    Early evening slots suit clients who can&rsquo;t get away during the working day.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">
                A nervous-system focused clinic
              </h3>
              <p className="text-gray-700 mb-4">
                The Arzen Collective brings together practitioners whose work centres on the nervous
                system. That makes it possible to build a pathway rather than book a one-off
                appointment — hypnotherapy alongside other complementary approaches, chosen around
                what you actually need. If stress, anxiety or pain is showing up in your body as
                well as your mind, that joined-up approach is often what makes the difference.
              </p>
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Online Sessions Alternative</h3>
              <p className="text-gray-700">
                Prefer to stay at home? Secure online hypnotherapy sessions provide the same
                professional treatment. This option suits those with childcare responsibilities, or
                anyone who values the convenience of online therapy. The therapeutic effectiveness is
                equivalent to in-person sessions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Lisburn Area Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "The drive from Lisburn was so easy on the M1. Sharon helped me quit smoking after 20 years. I scheduled my sessions on Thursday mornings before work, so it didn't interfere with my routine at all. Best decision I ever made - 8 months smoke-free and I've saved thousands of pounds already."
                </p>
                <p className="font-semibold text-gray-900">J.T., Lisburn - Quit Smoking</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Convenient for my commute. I booked my session on the way home from work in Belfast - literally added 10 minutes to my journey. Sharon's approach to work stress and anxiety was exactly what I needed. No more panic attacks, sleeping better, and I actually enjoy my commute now instead of dreading it."
                </p>
                <p className="font-semibold text-gray-900">A.K., Lisburn - Commuter Stress</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I'd tried every diet going - Weight Watchers, Slimming World, keto, you name it. Lost weight and put it back on every single time. Sharon's hypnotherapy approach was completely different - it addressed why I was comfort eating during stressful work weeks. Lost 2 stone over 6 months and it's stayed off for over a year."
                </p>
                <p className="font-semibold text-gray-900">S.M., Hillsborough - Weight Management</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "My back pain from years of commuting to Belfast was controlling my life. Medication wasn't helping enough. The hypnotherapy programme has made such a difference - pain levels reduced significantly and I've learned to manage flare-ups better. Worth every penny."
                </p>
                <p className="font-semibold text-gray-900">M.H., Moira - Chronic Back Pain</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I was heading for complete burnout - work stress, family pressures, constant anxiety. The online sessions from Sharon were perfect because I could do them from home in Dromore without adding more travel stress. My whole outlook has changed. I feel in control again instead of constantly overwhelmed."
                </p>
                <p className="font-semibold text-gray-900">L.P., Dromore - Anxiety and Burnout</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Lisburn Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Where exactly is the Lisburn clinic?
                </h3>
                <p className="text-gray-700">
                  Sessions in Lisburn take place at The Arzen Collective, 13B Antrim Street, Lisburn BT28 — in the centre of town. You can find directions and more about the clinic at{' '}
                  <a
                    href="https://arzencollective.co.uk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 hover:text-accent-700 underline"
                  >
                    arzencollective.co.uk
                  </a>
                  . If the Belfast clinic on the Ormeau Road suits you better, that remains available too, and online sessions are an alternative either way.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  When are appointments available in Lisburn?
                </h3>
                <p className="text-gray-700">
                  Appointment times vary from week to week, and early evening appointments are available — which works well for Lisburn professionals who can&rsquo;t get away during the working day. The best thing to do is get in touch and we&rsquo;ll find a time that fits. Online sessions offer additional flexibility if none of the in-person times suit.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do I still have to travel to Belfast?
                </h3>
                <p className="text-gray-700">
                  No. Hypnotherapy sessions are now available in Lisburn itself, at The Arzen Collective on Antrim Street. The Belfast clinic on the Ormeau Road is still there if you would rather go to that one, and online sessions remain available, but Lisburn clients no longer need to travel out of town.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I book sessions before or after work?
                </h3>
                <p className="text-gray-700">
                  Appointment times vary from week to week, so there is usually some flexibility — morning appointments before work, lunchtime sessions, afternoon slots, or early evening appointments, which are available and tend to suit clients who work standard hours. If none of the in-person times work for you, online sessions can be scheduled at times that suit you.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you offer online sessions for Lisburn clients?
                </h3>
                <p className="text-gray-700">
                  Yes, secure online hypnotherapy sessions are available via video call for Lisburn residents. Online sessions provide the same professional, evidence-based treatment as in-person appointments with the added convenience of attending from your home. This option is ideal for those with busy schedules, childcare responsibilities, mobility concerns, or anyone who simply prefers the comfort and convenience of online therapy. Many clients start with in-person sessions to build rapport and then switch to online for ongoing treatment.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with commuter stress?
                </h3>
                <p className="text-gray-700">
                  Absolutely. Commuter stress is one of the most common issues I treat among Lisburn clients. The daily journey to Belfast, traffic pressures, time pressures, and the cumulative stress of commuting year after year significantly impacts mental health. Solution-focused hypnotherapy works by reducing your overall anxiety levels, teaching you relaxation techniques you can use during your commute, improving your sleep quality (so you start each day less stressed), and building resilience to daily pressures. Most clients notice they feel calmer during their commute within just a few sessions.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does hypnotherapy help with weight loss?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy for weight management addresses the psychological and emotional reasons behind unhealthy eating patterns - something diets alone can't do. We work on understanding why you eat when you're not hungry, breaking the connection between stress/emotions and comfort eating, building genuine motivation for change, and creating lasting behavioral shifts. For busy Lisburn professionals dealing with work stress and time pressures, this approach is particularly effective because it addresses root causes rather than just restricting food. Most clients work through a 6-8 session programme with sustainable, lasting results.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What's your success rate for quit smoking?
                </h3>
                <p className="text-gray-700">
                  Success rates for smoking cessation with hypnotherapy are typically 60-80% for clients who are genuinely motivated to quit. The key factor is your readiness and commitment - if you truly want to become a non-smoker (rather than feeling you "should" quit), hypnotherapy is highly effective. The approach works by breaking the psychological addiction and changing your mindset from "giving up" something you enjoy to freeing yourself from something that controls you. Most clients complete a 3-4 session programme, with many quitting after the first or second session.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you work with NHS patients?
                </h3>
                <p className="text-gray-700">
                  Yes, I work with many clients who are also receiving NHS care. Hypnotherapy is complementary to medical treatment, not a replacement for it. Many Lisburn residents use hypnotherapy to supplement NHS care for conditions like anxiety, chronic pain, or stress-related health issues. Our proximity to Belfast City Hospital makes it particularly convenient for patients receiving medical treatment who need additional support with medical anxiety, pain management, or treatment-related stress. Always inform your medical team about any complementary therapies you're using.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How many sessions do I need?
                </h3>
                <p className="text-gray-700">
                  The number of sessions varies depending on your individual situation and goals. Most clients see significant improvements within 4-6 sessions for conditions like anxiety, stress, or smoking cessation. Weight management programmes typically involve 6-8 sessions. Chronic pain management or complex issues may benefit from 6-10 sessions. During your initial consultation, we'll assess your specific needs and create a tailored treatment plan. I also offer a discounted 4-session package which provides better value for clients committing to a programme of treatment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default LisburnHypnotherapy;
